import { coverPhotoFrame, type DesignBox, type ImageCrop } from '@pz/scene';

/**
 * The geometry of Zazzle's inline crop, in zone millimetres.
 *
 * On the canvas the whole photograph is shown, the layer's box is the bright
 * "window", and the shopper either slides the photo under the window (pan),
 * zooms it, or drags the window's edges (resize). Everything is expressed
 * through the shared `coverPhotoFrame`, so what the window shows is exactly
 * what the renderer will print — the editor never keeps a crop of its own.
 *
 * Only an upright layer with an upright crop is edited inline; a turned or
 * straightened photo keeps the full crop dialog, whose rotated frame this
 * axis-aligned window could not represent faithfully.
 */
export interface InlineCropState {
  box: DesignBox;
  crop: ImageCrop;
}

export interface ImageRectMm { left: number; top: number; w: number; h: number }

type Source = { w: number; h: number };

const MIN_FRAME_MM = 2;

export function canCropInline(layer: { box: DesignBox; crop?: ImageCrop; sourcePx?: Source; fit?: string }): boolean {
  return Boolean(layer.sourcePx) && !(layer.box.rotation ?? 0) && !(layer.crop?.rotation ?? 0) && layer.fit !== 'contain';
}

/** Where the whole photograph lies, in zone mm, for this box and crop. */
export function inlineImageRect(source: Source, state: InlineCropState, flipX = false, flipY = false): ImageRectMm {
  const frame = coverPhotoFrame(source, state.box, state.crop);
  const w = source.w * frame.scale;
  const h = source.h * frame.scale;
  // A mirrored layer shows the photo mirrored about the box centre.
  const u = flipX ? 1 - frame.crop.cx : frame.crop.cx;
  const v = flipY ? 1 - frame.crop.cy : frame.crop.cy;
  return { left: state.box.x - u * w, top: state.box.y - v * h, w, h };
}

/** Slides the photograph under the window by (dx, dy) mm on screen. */
export function inlinePan(source: Source, state: InlineCropState, dx: number, dy: number, flipX = false, flipY = false): InlineCropState {
  const frame = coverPhotoFrame(source, state.box, state.crop);
  const w = source.w * frame.scale;
  const h = source.h * frame.scale;
  const cx = frame.crop.cx - (flipX ? -dx : dx) / w;
  const cy = frame.crop.cy - (flipY ? -dy : dy) / h;
  return { box: state.box, crop: coverPhotoFrame(source, state.box, { ...frame.crop, cx, cy }).crop };
}

/** Zooms the photograph by `factor` about the window's centre, within 1–8. */
export function inlineZoom(source: Source, state: InlineCropState, factor: number): InlineCropState {
  const zoom = Math.max(1, Math.min(8, (state.crop.zoom ?? 1) * factor));
  return { box: state.box, crop: coverPhotoFrame(source, state.box, { ...state.crop, zoom }).crop };
}

/**
 * Drags one edge or corner of the window by (dx, dy) mm. The photograph stays
 * where it is on the page; the window may not leave it, and a crop that fills
 * the new window is derived from where the window now sits on the photo.
 */
export function inlineResize(
  source: Source,
  state: InlineCropState,
  handle: string,
  dx: number,
  dy: number,
  flipX = false,
  flipY = false,
): InlineCropState {
  const image = inlineImageRect(source, state, flipX, flipY);
  let left = state.box.x - state.box.w / 2;
  let right = state.box.x + state.box.w / 2;
  let top = state.box.y - state.box.h / 2;
  let bottom = state.box.y + state.box.h / 2;
  const clamp = (n: number, min: number, max: number): number => Math.max(min, Math.min(max, n));
  if (handle.includes('w')) left = clamp(left + dx, image.left, right - MIN_FRAME_MM);
  if (handle.includes('e')) right = clamp(right + dx, left + MIN_FRAME_MM, image.left + image.w);
  if (handle.includes('n')) top = clamp(top + dy, image.top, bottom - MIN_FRAME_MM);
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + MIN_FRAME_MM, image.top + image.h);
  const box: DesignBox = { ...state.box, x: (left + right) / 2, y: (top + bottom) / 2, w: right - left, h: bottom - top };
  // The photo keeps its size on the page: the same mm per source pixel.
  const scale = image.w / source.w;
  const zoom = scale / Math.max(box.w / source.w, box.h / source.h);
  const u = (box.x - image.left) / image.w;
  const v = (box.y - image.top) / image.h;
  const crop = coverPhotoFrame(source, box, {
    cx: flipX ? 1 - u : u,
    cy: flipY ? 1 - v : v,
    zoom: Math.max(1, zoom),
  }).crop;
  return { box, crop };
}
